import {
  ProductAccessLimits,
  ProductAccessPeriodUsage,
} from '../../../../server/graphql/__generated.types';

export type DownloadMinutesUsage = {
  usageMinutes: number;
  limitMinutes: number;
  remainingMinutes: number;
  remainingMs: number;
  usagePercentage: number;
  periodEnd: number;
};

type DownloadMinutesLimits = Pick<
  ProductAccessLimits,
  'takeDurationMsDownloadLimit' | 'takeDurationMsGenerationLimit'
>;

type DownloadMinutesPeriodUsage = Pick<
  ProductAccessPeriodUsage,
  'downloadedTakesDurationMs' | 'generatedTakesDurationMs' | 'periodEnd'
>;

export type ProductUsageAccessScope = {
  productAccessLimits: DownloadMinutesLimits;
  productAccessPeriodUsage: DownloadMinutesPeriodUsage;
};

export type ProductUsageUserAccess = ProductUsageAccessScope & {
  team?: ProductUsageAccessScope | null;
};

export const getProductUsageAccessScope = (
  user: ProductUsageUserAccess
): ProductUsageAccessScope => {
  const access = user.team ? user.team : user;

  return {
    productAccessLimits: access.productAccessLimits,
    productAccessPeriodUsage: access.productAccessPeriodUsage,
  };
};

type ResolvedDurationLimit = {
  limitMs: number;
  usageMs: number;
};

export const isMeteredUser = (limits?: DownloadMinutesLimits | null): boolean =>
  typeof limits?.takeDurationMsDownloadLimit === 'number' ||
  typeof limits?.takeDurationMsGenerationLimit === 'number';

export const isMeteredProductUsageUser = (
  user?: ProductUsageUserAccess | null
): boolean =>
  user ? isMeteredUser(getProductUsageAccessScope(user).productAccessLimits) : false;

const resolveDurationLimit = (
  limits: DownloadMinutesLimits,
  usage: DownloadMinutesPeriodUsage
): ResolvedDurationLimit | null => {
  if (!isMeteredUser(limits)) {
    return null;
  }

  return {
    limitMs: limits.takeDurationMsDownloadLimit ?? 0,
    usageMs: usage.downloadedTakesDurationMs ?? 0,
  };
};

export const getDownloadMinutesUsage = (
  limits: DownloadMinutesLimits,
  usage: DownloadMinutesPeriodUsage
): DownloadMinutesUsage | null => {
  const resolved = resolveDurationLimit(limits, usage);
  if (!resolved) {
    return null;
  }

  const { limitMs, usageMs: usageMsValue } = resolved;
  const limitMinutes = Math.floor(limitMs / 60000);
  const usageMinutes = Math.floor(usageMsValue / 60000);
  const remainingMs = Math.max(0, limitMs - usageMsValue);
  const remainingMinutes = Math.floor(remainingMs / 60000);
  const usagePercentage =
    limitMs === 0 ? 100 : Math.min(100, (usageMsValue / limitMs) * 100);

  return {
    usageMinutes,
    limitMinutes,
    remainingMinutes,
    remainingMs,
    usagePercentage,
    periodEnd: usage.periodEnd,
  };
};

export type DownloadMinutesCounterDisplay = {
  limitMs: number;
  usageMs: number;
  limit: string;
  usage: string;
};

const formatCounterUsageDisplay = (usageMs: number) => {
  if (usageMs >= 60000) {
    return Math.floor(usageMs / 60000).toString();
  }

  const totalSeconds = Math.floor(usageMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

export const getDownloadMinutesCounterDisplay = (
  limits: DownloadMinutesLimits,
  usage: DownloadMinutesPeriodUsage
): DownloadMinutesCounterDisplay | null => {
  const resolved = resolveDurationLimit(limits, usage);
  if (!resolved) {
    return null;
  }

  const { limitMs, usageMs } = resolved;

  return {
    limitMs,
    usageMs,
    limit: Math.floor(limitMs / 60000).toString(),
    usage: formatCounterUsageDisplay(usageMs),
  };
};

export const DEFAULT_DOWNLOAD_MINUTES_WARNING_THRESHOLD = 80;

export const isDownloadLimitReached = (
  usage: Pick<DownloadMinutesUsage, 'remainingMinutes'>
) => usage.remainingMinutes <= 0;

export const isDownloadUsageWarning = (
  usage: Pick<DownloadMinutesUsage, 'remainingMinutes' | 'usagePercentage'>,
  warningThreshold = DEFAULT_DOWNLOAD_MINUTES_WARNING_THRESHOLD
) =>
  !isDownloadLimitReached(usage) && usage.usagePercentage >= warningThreshold;

export type DownloadMinutesProgressBarVariant =
  | 'limitReached'
  | 'warning'
  | 'normal';

export const getDownloadMinutesProgressBarVariant = (
  usage: Pick<DownloadMinutesUsage, 'remainingMinutes' | 'usagePercentage'>,
  warningThreshold = DEFAULT_DOWNLOAD_MINUTES_WARNING_THRESHOLD
): DownloadMinutesProgressBarVariant => {
  if (isDownloadLimitReached(usage)) {
    return 'limitReached';
  }
  if (isDownloadUsageWarning(usage, warningThreshold)) {
    return 'warning';
  }
  return 'normal';
};
